/**
 * MCP Tools for Tasks Management
 */

import { MCPTool } from '../types';
import { tasksService } from '../../services/tasksService';
import { Task } from '../../types';
import { getMCPServer } from '../server/MCPServer';

export const createTaskTool: MCPTool = {
  name: 'create_task',
  description: 'Creates a new task/to-do. Use when user says: "Erstelle Task", "Neue Aufgabe", "Erinnere mich an", "Ich muss noch". Can assign to users, set priority, due date. Returns created task with ID.',
  
  inputSchema: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        description: 'Task title (required)'
      },
      description: {
        type: 'string',
        description: 'Detailed description of the task'
      },
      dueDate: {
        type: 'string',
        description: 'Due date in ISO 8601 format (e.g., "2025-12-31")'
      },
      priority: {
        type: 'string',
        enum: ['low', 'medium', 'high'],
        description: 'Task priority level'
      },
      type: {
        type: 'string',
        enum: ['call', 'email', 'meeting', 'follow_up', 'presentation', 'todo', 'technical'],
        description: 'Task type (defaults to "todo")'
      },
      status: {
        type: 'string',
        enum: ['pending', 'in_progress', 'completed', 'blocked'],
        description: 'Task status'
      },
      assignedTo: {
        type: 'string',
        description: 'User ID to assign the task to'
      },
      relatedLeadId: {
        type: 'string',
        description: 'ID of related lead'
      },
      relatedOpportunityId: {
        type: 'string',
        description: 'ID of related opportunity'
      }
    },
    required: ['title']
  },

  async execute(params, context) {
    const task: Omit<Task, 'id'> = {
      title: params.title,
      description: params.description || '',
      dueDate: params.dueDate ? new Date(params.dueDate) : new Date(),
      priority: params.priority || 'medium',
      type: params.type || 'todo',
      status: params.status || 'pending',
      completed: false,
      assignedTo: params.assignedTo || context.userId,
      createdBy: context.userId,
      orgId: context.orgId,
      visibility: 'public',
      createdAt: new Date(),
      updatedAt: new Date(),
      leadId: params.relatedLeadId || '',
      contactId: params.relatedLeadId || undefined,
      opportunityId: params.relatedOpportunityId || undefined,
    };

    const createdTask = await tasksService.createTask(task);

    return {
      success: true,
      task: createdTask,
      message: `Task "${createdTask.title}" created successfully`
    };
  }
};

export const listTasksTool: MCPTool = {
  name: 'list_tasks',
  description: 'Lists the current user\'s tasks by default. Use when user asks: "Welche Tasks habe ich?", "Zeig meine Tasks", "Was steht heute an?", "Offene Aufgaben". Supports filters: status, priority, assignedTo, dueToday, overdue. Only use assignedTo when a specific user is requested.',
  
  inputSchema: {
    type: 'object',
    properties: {
      status: {
        type: 'string',
        enum: ['pending', 'in_progress', 'completed', 'cancelled'],
        description: 'Filter by status'
      },
      priority: {
        type: 'string',
        enum: ['low', 'medium', 'high', 'urgent'],
        description: 'Filter by priority'
      },
      assignedTo: {
        type: 'string',
        description: 'Filter by assigned user ID'
      },
      dueToday: {
        type: 'boolean',
        description: 'Only show tasks due today'
      },
      overdue: {
        type: 'boolean',
        description: 'Only show overdue tasks'
      }
    }
  },

  async execute(params, context) {
    let tasks = await tasksService.getTasks(context.userId, context.orgId);

    if (!params.assignedTo) {
      tasks = tasks.filter(t => t.assignedTo === context.userId || t.createdBy === context.userId);
    }

    if (params.status) {
      tasks = tasks.filter(t => t.status === params.status);
    }

    if (params.priority) {
      tasks = tasks.filter(t => t.priority === params.priority);
    }

    if (params.assignedTo) {
      tasks = tasks.filter(t => t.assignedTo === params.assignedTo);
    }

    if (params.dueToday) {
      const today = new Date().toDateString();
      tasks = tasks.filter(t => new Date(t.dueDate).toDateString() === today);
    }

    if (params.overdue) {
      const now = new Date();
      tasks = tasks.filter(t => new Date(t.dueDate) < now && t.status !== 'completed');
    }

    return {
      success: true,
      tasks,
      count: tasks.length
    };
  }
};

export const updateTaskTool: MCPTool = {
  name: 'update_task',
  description: 'Updates an existing task. Use when user says: "Ändere Task", "Update Aufgabe", "Setze Priorität auf high", "Verschiebe Task". Can update title, description, status, priority, due date.',
  
  inputSchema: {
    type: 'object',
    properties: {
      taskId: {
        type: 'string',
        description: 'Task ID to update'
      },
      title: {
        type: 'string',
        description: 'Updated title'
      },
      description: {
        type: 'string',
        description: 'Updated description'
      },
      status: {
        type: 'string',
        enum: ['pending', 'in_progress', 'completed', 'cancelled'],
        description: 'Updated status'
      },
      priority: {
        type: 'string',
        enum: ['low', 'medium', 'high', 'urgent'],
        description: 'Updated priority'
      },
      dueDate: {
        type: 'string',
        description: 'Updated due date'
      }
    },
    required: ['taskId']
  },

  async execute(params, context) {
    const { taskId, ...updates } = params;
    
    if (updates.dueDate) {
      updates.dueDate = new Date(updates.dueDate);
    }

    await tasksService.updateTask(taskId, {
      ...updates,
      updatedAt: new Date()
    });

    return {
      success: true,
      taskId: taskId,
      updates: updates,
      message: `Task updated successfully`
    };
  }
};

export const completeTaskTool: MCPTool = {
  name: 'complete_task',
  description: 'Marks a task as completed/done. Use when user says: "Task erledigt", "Markiere als fertig", "Complete Task", "Schließe Aufgabe ab". Sets status to completed and adds completion timestamp.',
  
  inputSchema: {
    type: 'object',
    properties: {
      taskId: {
        type: 'string',
        description: 'Task ID to complete'
      }
    },
    required: ['taskId']
  },

  async execute(params, context) {
    await tasksService.updateTask(params.taskId, {
      status: 'completed',
      completedAt: new Date(),
      updatedAt: new Date()
    });

    return {
      success: true,
      taskId: params.taskId,
      status: 'completed',
      message: `Task marked as completed`
    };
  }
};

export function registerTaskTools(): void {
  const server = getMCPServer();
  
  server.registerTool(createTaskTool, { category: 'tasks', requiresAuth: true, dangerLevel: 'safe' });
  server.registerTool(listTasksTool, { category: 'tasks', requiresAuth: true, dangerLevel: 'safe' });
  server.registerTool(updateTaskTool, { category: 'tasks', requiresAuth: true, dangerLevel: 'moderate' });
  server.registerTool(completeTaskTool, { category: 'tasks', requiresAuth: true, dangerLevel: 'safe' });
  
  console.log('✅ Registered 4 task management tools');
}
